import { parseDate, formatDate, fromUnixSeconds, getUserAlias } from 'shared-components';
import { Temporal } from '@js-temporal/polyfill';
import { AuthState } from '../store/auth/types';
import { IUniConfig, EventInfoStatus, VisaStatus, IFormattedEventTypeDate, RecordedInterviewSystemStatus } from 'api-paths-cas';
import { universityKeys } from 'configuration-data';
export function normalizeToFullYear(year: string | null): number {
  const yearNum = Number(year);
  return yearNum < 50 ? 2000 + yearNum : yearNum < 100 ? 1900 + yearNum : yearNum;
}

export const getVisaStatusFormat = (
  value: string,
): {
  onHoverText: string;
  colorScheme: string;
} => {
  let colorScheme = '';
  let onHoverText = '';

  switch (value) {
    case VisaStatus.CAS_NOT_RECEIVED: {
      onHoverText = 'The student is yet to receive their CAS';
      colorScheme = 'red';
      break;
    }
    case VisaStatus.CAS_RECEIVED: {
      onHoverText = 'The student has their CAS but are yet to submit their visa';
      colorScheme = 'yellow';
      break;
    }
    case VisaStatus.VISA_APPLIED: {
      onHoverText = "The student's visa application has been submitted";
      colorScheme = 'cyan';
      break;
    }
    case VisaStatus.AWAITING_VISA_RESPONSE: {
      onHoverText = "The student's visa application has been submitted";
      colorScheme = 'cyan';
      break;
    }
    case VisaStatus.UKVI_REQUEST_MORE_INFO: {
      onHoverText = 'UKVI has requested more information from the student';
      colorScheme = 'yellow';
      break;
    }
    case VisaStatus.VISA_RECEIVED: {
      onHoverText = 'The student has received their visa';
      colorScheme = 'green';
      break;
    }
    case VisaStatus.VISA_REFUSAL: {
      onHoverText = 'The student has received a visa refusal';
      colorScheme = 'red';
      break;
    }
    case VisaStatus.INTENDS_TO_DEFER: {
      onHoverText = 'The student intends to defer to the next intake';
      colorScheme = 'yellow';
      break;
    }
    case VisaStatus.INTENDS_TO_WITHDRAW: {
      onHoverText = 'The student has indicated they wish to withdraw';
      colorScheme = 'yellow';
      break;
    }
    case VisaStatus.NONE: {
      onHoverText = `The student hasn't updated their visa status`;
      colorScheme = 'yellow';
      break;
    }
  }
  return {
    onHoverText,
    colorScheme,
  };
};

export const getUniFullName = (key: string): string => {
  const university = universityKeys.find((item: any) => item.key === key);
  return university?.name ?? '';
};

export const getDisplayName = (auth: AuthState, _config: IUniConfig): string => {
  const username = auth.student_number!;
  const userAlias = getUserAlias();
  const givenName = auth?.currentUser?.givenName || '';
  // Prefer alias, then given name, then fall back to username
  return userAlias || givenName || username;
};

export const getInterviewInfoStatusString = (
  infoStatus: EventInfoStatus,
  calendlyInterviewStartDate?: number,
  calendlyInterviewEndDate?: number,
) => {
  switch (infoStatus) {
    case EventInfoStatus.INVITED:
      return 'Invited';
    case EventInfoStatus.BOOKED:
      if (!calendlyInterviewStartDate || !calendlyInterviewEndDate) return 'Booked';

      const startInstant = fromUnixSeconds(Math.floor(calendlyInterviewStartDate / 1000));
      const endInstant = fromUnixSeconds(Math.floor(calendlyInterviewEndDate / 1000));
      const startDateTime = startInstant.toZonedDateTimeISO('UTC').toPlainDateTime();
      const endDateTime = endInstant.toZonedDateTimeISO('UTC').toPlainDateTime();

      const startFormatted =
        `${formatDate(startDateTime.toPlainDate(), 'DD MMM YYYY')} ` +
        `${String(startDateTime.hour).padStart(2, '0')}:${String(startDateTime.minute).padStart(2, '0')}`;
      const endFormatted = `${String(endDateTime.hour).padStart(2, '0')}:${String(endDateTime.minute).padStart(
        2,
        '0',
      )}`;

      return `Booked ${startFormatted}-${endFormatted}`;
    case EventInfoStatus.PASSED:
      return 'Passed';
    case EventInfoStatus.FAILED:
      return 'Failed';
    case EventInfoStatus.RESIT:
      return 'Resit';
    case EventInfoStatus.NONE:
      return 'None';
    default:
      return '';
  }
};

export const getInterviewInfoStatusColor = (infoStatus: EventInfoStatus) => {
  switch (infoStatus) {
    case EventInfoStatus.INVITED:
      return { variant: 'transparent' };
    case EventInfoStatus.BOOKED:
      return { colorScheme: 'cyan' };
    case EventInfoStatus.PASSED:
      return { colorScheme: 'green' };
    case EventInfoStatus.FAILED:
      return { colorScheme: 'red' };
    case EventInfoStatus.RESIT:
      return { colorScheme: 'yellow' };
    case EventInfoStatus.NONE:
      return { variant: 'transparent' };
    default:
      return { variant: 'transparent' };
  }
};

export const getRecordedInterviewInfoStatusString = (
  infoStatus: RecordedInterviewSystemStatus,
  recordedInterviewTimestamp?: string | number,
) => {
  switch (infoStatus) {
    case RecordedInterviewSystemStatus.INVITED:
      return 'Invited';
    case RecordedInterviewSystemStatus.RECORDED:
      if (!recordedInterviewTimestamp) return 'Recorded';

      // Convert to string if it's a number
      const timestampStr = String(recordedInterviewTimestamp);

      // Parse the timestamp string (could be ISO date or unix timestamp)
      let dateTime: Temporal.PlainDateTime;
      if (timestampStr.includes('-') || timestampStr.includes('T')) {
        // ISO date string
        const date = parseDate(timestampStr);
        if (!date) return 'Recorded';
        dateTime = date.toPlainDateTime();
      } else {
        // Unix timestamp
        const timestampMs = parseInt(timestampStr);
        if (isNaN(timestampMs)) return 'Recorded';
        const instant = fromUnixSeconds(Math.floor(timestampMs / 1000));
        dateTime = instant.toZonedDateTimeISO('UTC').toPlainDateTime();
      }

      const dateFormatted = formatDate(dateTime.toPlainDate(), 'DD MMM YYYY');
      const timeFormatted = `${String(dateTime.hour).padStart(2, '0')}:${String(dateTime.minute).padStart(2, '0')}`;

      return `Recorded ${dateFormatted} ${timeFormatted}`;
    case RecordedInterviewSystemStatus.PASSED:
      return 'Passed';
    case RecordedInterviewSystemStatus.FAILED:
      return 'Failed';
    case RecordedInterviewSystemStatus.RESIT:
      return 'Resit';
    case RecordedInterviewSystemStatus.ATTEMPTED:
      return 'Attempted';
    case RecordedInterviewSystemStatus.DELETED:
      return 'Deleted';
    case RecordedInterviewSystemStatus.NONE:
      return 'None';
    default:
      return 'None';
  }
};

export const getRecordedInterviewInfoStatusColor = (infoStatus: RecordedInterviewSystemStatus) => {
  switch (infoStatus) {
    case RecordedInterviewSystemStatus.INVITED:
      return { variant: 'transparent' };
    case RecordedInterviewSystemStatus.RECORDED:
      return { colorScheme: 'cyan' };
    case RecordedInterviewSystemStatus.PASSED:
      return { colorScheme: 'green' };
    case RecordedInterviewSystemStatus.FAILED:
      return { colorScheme: 'red' };
    case RecordedInterviewSystemStatus.RESIT:
      return { colorScheme: 'yellow' };
    case RecordedInterviewSystemStatus.ATTEMPTED:
      return { variant: 'transparent' };
    case RecordedInterviewSystemStatus.NONE:
      return { variant: 'transparent' };
    default:
      return { variant: 'transparent' };
  }
};

export const getFormattedDateTime = (startDateMs?: number, endDateMs?: number): string => {
  if (!startDateMs || !endDateMs) {
    return '';
  }

  const startInstant = fromUnixSeconds(Math.floor(startDateMs / 1000));
  const endInstant = fromUnixSeconds(Math.floor(endDateMs / 1000));

  // Convert to PlainDateTime in UTC timezone
  const startDateTime = startInstant.toZonedDateTimeISO('UTC').toPlainDateTime();
  const endDateTime = endInstant.toZonedDateTimeISO('UTC').toPlainDateTime();

  const date = formatDate(startDateTime.toPlainDate(), 'DD MMM YYYY');
  const startTime = `${String(startDateTime.hour).padStart(2, '0')}:${String(startDateTime.minute).padStart(2, '0')}`;
  const endTime = `${String(endDateTime.hour).padStart(2, '0')}:${String(endDateTime.minute).padStart(2, '0')}`;
  const tz = '+0000'; // UTC timezone offset

  return `on ${date} at ${startTime}-${endTime} (GMT${tz})`;
};

export const getFormattedDateTimeV2 = (
  startDateMs?: number,
  endDateMs?: number,
): IFormattedEventTypeDate | undefined => {
  if (!startDateMs || !endDateMs) {
    return undefined;
  }

  const startInstant = fromUnixSeconds(Math.floor(startDateMs / 1000));
  const endInstant = fromUnixSeconds(Math.floor(endDateMs / 1000));

  // Convert to PlainDateTime in UTC timezone
  const startDateTime = startInstant.toZonedDateTimeISO('UTC').toPlainDateTime();
  const endDateTime = endInstant.toZonedDateTimeISO('UTC').toPlainDateTime();

  const date = formatDate(startDateTime.toPlainDate(), 'DD MMM YYYY');
  const startTime = `${String(startDateTime.hour).padStart(2, '0')}:${String(startDateTime.minute).padStart(2, '0')}`;
  const endTime = `${String(endDateTime.hour).padStart(2, '0')}:${String(endDateTime.minute).padStart(2, '0')}`;
  const tz = '+0000'; // UTC timezone offset

  return {
    date,
    startTime,
    endTime,
    tz,
  };
};

export const getFormattedDateTimeRecordedInterview = (dateMs?: number): string | undefined => {
  if (!dateMs) {
    return undefined;
  }

  const instant = fromUnixSeconds(Math.floor(dateMs / 1000));
  const dateTime = instant.toZonedDateTimeISO('UTC').toPlainDateTime();

  const dateFormatted = formatDate(dateTime.toPlainDate(), 'DD MMM YYYY');
  const timeFormatted = `${String(dateTime.hour).padStart(2, '0')}:${String(dateTime.minute).padStart(2, '0')}`;

  return `${dateFormatted} ${timeFormatted} +0000`;
};

export const formatCurrency = (value: string | number): string => {
  if (typeof value === 'number') {
    value = value.toString();
  } else if (!value) {
    value = '';
  }
  const hasCents = value.split('.').length > 1;
  let cents = '';
  if (hasCents) {
    const splitValue = value.split('.');
    cents = splitValue[1];
    value = splitValue[0];
  }
  const chars = value.split('').reverse();
  const withCommas = [];
  for (let i = 1, l = chars.length; i <= l; i += 1) {
    withCommas.push(chars[i - 1]);
    if (i % 3 === 0 && i !== chars.length) {
      withCommas.push(',');
    }
  }
  return `${withCommas.reverse().join('')}${hasCents ? `.${cents}` : ''}`;
};

export const reverseFormatCurrency = (value = ''): string => {
  return value.replace(/,/gi, '');
};

export const slug = (value: string): string => {
  if (!value) {
    return '';
  }
  return value.toLowerCase().replace(/ /g, '-');
};

export const formatWithDecimal = (value: string | number): string => {
  let num = getNumber(value);

  if (num === null) {
    return '';
  }
  num = +num.toFixed(2);
  return formatCurrency(num);
};

export const formatYears = (value: string | number): string => {
  const num = getNumber(value);
  if (num === null) {
    return '';
  }
  return `${+(num / 365).toFixed(2)}y`;
};

export const getNumber = (value: string | number): number | null => {
  if (typeof value !== 'number') {
    value = Number(value);
  }
  if (isNaN(value)) {
    return null;
  }
  return value;
};

export function boolToYesNo(val?: boolean): '' | 'Yes' | 'No' {
  return val !== undefined ? (val ? 'Yes' : 'No') : '';
}

export const getScheduledInterviewsInfoStatusDisplay = (
  scheduledInterviewsInfoStatus: EventInfoStatus,
  isActive: boolean,
  isAgentOrAgencyAdmin: boolean,
  isScheduledInterviewsVisible: boolean,
  isPartnerUniversityOrAdmin: boolean,
): {
  label: string;
  textColor: string;
  priority: number;
} => {
  const textColor: string = isActive ? '#ffffff' : '#3a3a3a';

  switch (scheduledInterviewsInfoStatus) {
    case EventInfoStatus.INVITED:
      return {
        label: isScheduledInterviewsVisible && isPartnerUniversityOrAdmin ? 'Invited' : 'None',
        textColor,
        priority: isScheduledInterviewsVisible && isPartnerUniversityOrAdmin ? 1 : 8,
      };
    case EventInfoStatus.BOOKED:
      return {
        label: isScheduledInterviewsVisible && isPartnerUniversityOrAdmin ? 'Booked' : 'None',
        textColor,
        priority: isScheduledInterviewsVisible && isPartnerUniversityOrAdmin ? 2 : 8,
      };
    case EventInfoStatus.FAILED:
      return {
        label:
          isScheduledInterviewsVisible && isPartnerUniversityOrAdmin
            ? 'Failed'
            : isAgentOrAgencyAdmin
              ? 'Booked'
              : 'None',
        textColor: isAgentOrAgencyAdmin ? textColor : '#f5a07d',
        priority: isScheduledInterviewsVisible && isPartnerUniversityOrAdmin ? 4 : isAgentOrAgencyAdmin ? 2 : 8,
      };
    case EventInfoStatus.PASSED:
      return {
        label:
          isScheduledInterviewsVisible && isPartnerUniversityOrAdmin
            ? 'Passed'
            : isAgentOrAgencyAdmin
              ? 'Booked'
              : 'None',
        textColor: isAgentOrAgencyAdmin ? textColor : '#5dcbad',
        priority: isScheduledInterviewsVisible && isPartnerUniversityOrAdmin ? 5 : isAgentOrAgencyAdmin ? 2 : 8,
      };
    case EventInfoStatus.RESIT:
      return {
        label:
          isScheduledInterviewsVisible && isPartnerUniversityOrAdmin
            ? 'Resit'
            : isAgentOrAgencyAdmin
              ? 'Booked'
              : 'None',
        textColor: isAgentOrAgencyAdmin ? textColor : '#dcb13d',
        priority: isScheduledInterviewsVisible && isPartnerUniversityOrAdmin ? 6 : isAgentOrAgencyAdmin ? 2 : 8,
      };
    case EventInfoStatus.NONE:
      return { label: 'None', textColor: '#b4b4b4', priority: 8 };
    default:
      return { label: 'None', textColor: '#b4b4b4', priority: 8 };
  }
};

export const getRecordedInterviewSystemStatusDisplay = (
  recordedInterviewSystemStatus: RecordedInterviewSystemStatus,
  isActive: boolean,
  isAgentOrAgencyAdmin: boolean,
  isRecordedInterviewVisible: boolean,
  isPartnerUniversityOrAdmin: boolean,
): {
  label: string;
  textColor: string;
  priority: number;
} => {
  const textColor: string = isActive ? '#ffffff' : '#3a3a3a';

  switch (recordedInterviewSystemStatus) {
    case RecordedInterviewSystemStatus.INVITED:
      return {
        label: isRecordedInterviewVisible && isPartnerUniversityOrAdmin ? 'Invited' : 'None',
        textColor,
        priority: isRecordedInterviewVisible && isPartnerUniversityOrAdmin ? 1 : 8,
      };
    case RecordedInterviewSystemStatus.RECORDED:
      return {
        label: isRecordedInterviewVisible && isPartnerUniversityOrAdmin ? 'Recorded' : 'None',
        textColor,
        priority: isRecordedInterviewVisible && isPartnerUniversityOrAdmin ? 3 : 8,
      };
    case RecordedInterviewSystemStatus.FAILED:
      return {
        label:
          isRecordedInterviewVisible && isPartnerUniversityOrAdmin
            ? 'Failed'
            : isAgentOrAgencyAdmin
              ? 'Recorded'
              : 'None',
        textColor: isAgentOrAgencyAdmin ? textColor : '#f5a07d',
        priority: isRecordedInterviewVisible && isPartnerUniversityOrAdmin ? 4 : isAgentOrAgencyAdmin ? 3 : 8,
      };
    case RecordedInterviewSystemStatus.PASSED:
      return {
        label:
          isRecordedInterviewVisible && isPartnerUniversityOrAdmin
            ? 'Passed'
            : isAgentOrAgencyAdmin
              ? 'Recorded'
              : 'None',
        textColor: isAgentOrAgencyAdmin ? textColor : '#5dcbad',
        priority: isRecordedInterviewVisible && isPartnerUniversityOrAdmin ? 5 : isAgentOrAgencyAdmin ? 3 : 8,
      };
    case RecordedInterviewSystemStatus.RESIT:
      return {
        label:
          isRecordedInterviewVisible && isPartnerUniversityOrAdmin
            ? 'Resit'
            : isAgentOrAgencyAdmin
              ? 'Recorded'
              : 'None',
        textColor: isAgentOrAgencyAdmin ? textColor : '#dcb13d',
        priority: isRecordedInterviewVisible && isPartnerUniversityOrAdmin ? 6 : isAgentOrAgencyAdmin ? 3 : 8,
      };
    case RecordedInterviewSystemStatus.ATTEMPTED:
      return {
        label: isRecordedInterviewVisible && isPartnerUniversityOrAdmin ? 'Attempted' : 'None',
        textColor: '#b4b4b4',
        priority: isRecordedInterviewVisible && isPartnerUniversityOrAdmin ? 7 : 8,
      };
    case RecordedInterviewSystemStatus.NONE:
      return { label: 'None', textColor: '#b4b4b4', priority: 8 };
    default:
      return { label: 'None', textColor: '#b4b4b4', priority: 8 };
  }
};
